import { useQuery } from '@apollo/client';
import { Loader } from '@volumental/common';
import { type JSX, useEffect, useMemo } from 'react';
import { graphql } from '../../api/gql';
import { doesSessionExist } from '../../auth';
import { FlowNotSupported } from '../../navigation/flow-not-supported';
import { useViewStore } from '../../store';
import { CurrentView } from '../../types/types';
import { GeneralErrorPage } from '../error/GeneralErrorPage';
import { SelectScan } from './select-scan';
import { SignIn } from './sign-in';
import { ValidateAccount } from './validate-account';

const GetHistoryConfigurationQuery = graphql(`
  query getHistoryConfigurationQuery {
    configuration {
      features {
        enableProfiles
      }
    }
  }
`);
export interface HistoryFlowProps {
  onClose: () => void;
}

export function HistoryFlow({ onClose }: HistoryFlowProps): JSX.Element {
  const { loading, error, data } = useQuery(GetHistoryConfigurationQuery);
  if (loading) {
    return <Loader />;
  }
  if (error || !data) {
    return (
      <GeneralErrorPage
        errorText="Missing HistoryFlow configuration"
        onClose={onClose}
      />
    );
  }
  if (!data.configuration.features.enableProfiles) {
    return <FlowNotSupported />;
  }
  return <HistoryFlowContent onClose={onClose} />;
}

const HistoryFlowContent = ({ onClose }: HistoryFlowProps): JSX.Element => {
  const { currentView, setCurrentView } = useViewStore((store) => store);

  useEffect(() => {
    void doesSessionExist().then((hasSession) => {
      if (hasSession) {
        setCurrentView(CurrentView.HistorySelectScan);
      } else {
        setCurrentView(CurrentView.HistorySignIn);
      }
    });
  }, [setCurrentView]);

  return useMemo(() => {
    if (currentView === CurrentView.HistorySignIn) {
      return (
        <SignIn
          changeFlow={() => {
            setCurrentView(CurrentView.Intro);
          }}
          onValidate={() => {
            setCurrentView(CurrentView.HistoryValidateAccount);
          }}
          onClose={onClose}
        />
      );
    }
    if (currentView === CurrentView.HistoryValidateAccount) {
      return (
        <ValidateAccount
          onValidated={() => {
            setCurrentView(CurrentView.HistorySelectScan);
          }}
          onRestart={() => {
            setCurrentView(CurrentView.HistorySignIn);
          }}
          onClose={onClose}
        />
      );
    }
    if (currentView === CurrentView.HistorySelectScan) {
      return (
        <SelectScan
          onRestart={() => {
            setCurrentView(CurrentView.HistorySignIn);
          }}
          onClose={onClose}
        />
      );
    }

    throw new Error(`Invalid view ${currentView ?? 'undefined'}`);
  }, [currentView, onClose, setCurrentView]);
};
